import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { api } from '../api/client'
import type { AddressInventorySettings, AddressRecordInfo, Probe } from '../api/types'
import { usePermessiModuli } from '../auth/permessi'
import { Card, EmptyState, ErrorBanner, Spinner, StatCard, formatDateTime } from '../components/common'
import { DataTable, type Column } from '../components/DataTable'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

/**
 * Rete → Inventario indirizzi: every address seen in the traffic that is not
 * a CI — the internal ones nobody inventoried and the internet services the
 * network talks to — with its reverse DNS name and, written by a person, what
 * it is, a category and an owner.
 *
 * Rows come from the flows every 15 minutes (address_inventory_service). The
 * PTR is asked to a probe of the tenant (internal DNS) or, for public
 * addresses, to the Control Plane, and kept for the TTL the DNS gave (15 min –
 * 7 days). A row can be documented in phpIPAM («Documenta su IPAM») when a
 * phpIPAM credential has writing enabled.
 */

/** The categories a person can give an address (the backend's CATEGORIES, '' = none). */
const CATEGORIE = ['servizio_esterno', 'cloud', 'fornitore', 'dispositivo', 'utente', 'rete', 'sospetto', 'altro']

/** The most addresses one «resolve» call accepts (as the API). */
const MAX_RISOLVI = 500

/** Ports shown in the cell before «+N». */
const PORTE_MOSTRATE = 5

type Vista = 'tutti' | 'interni' | 'pubblici' | 'con_nome' | 'descritti' | 'ci' | 'errori'

type Tono = 'ok' | 'warn' | 'error'

interface Esito {
  tono: Tono
  testo: string
}

interface ServerIpam {
  id: string
  name: string
  write_enabled?: boolean
}

function etichettaCategoria(c: string): string {
  const etichette: Record<string, string> = {
    servizio_esterno: t('servizio esterno'),
    cloud: t('cloud'),
    fornitore: t('fornitore'),
    dispositivo: t('dispositivo'),
    utente: t('utente'),
    rete: t('rete'),
    sospetto: t('sospetto'),
    altro: t('altro'),
  }
  return etichette[c] ?? c
}

function etichettaAmbito(s: string): string {
  const etichette: Record<string, string> = {
    interno: t('interno'),
    pubblico: t('pubblico'),
    speciale: t('speciale'),
  }
  return etichette[s] ?? s
}

function etichettaStatoPtr(r: AddressRecordInfo): string {
  if (r.ptr_name) return r.ptr_name
  if (r.scope === 'speciale') return ''
  if (r.ptr_status === 'nessuno') return t('nessun record PTR')
  if (r.ptr_status === 'errore') return t('non risolto')
  if (r.ptr_status === 'da_fare') return t('da risolvere')
  return ''
}

function etichettaVista(v: Vista): string {
  const etichette: Record<Vista, string> = {
    tutti: '',
    interni: t('indirizzi interni'),
    pubblici: t('indirizzi pubblici'),
    con_nome: t('con un nome DNS'),
    descritti: t('descritti da una persona'),
    ci: t('che ora sono CI'),
    errori: t('con un errore di risoluzione'),
  }
  return etichette[v]
}

function nellaVista(r: AddressRecordInfo, v: Vista): boolean {
  switch (v) {
    case 'interni': return r.scope === 'interno'
    case 'pubblici': return r.scope === 'pubblico'
    case 'con_nome': return r.ptr_status === 'ok'
    case 'descritti': return Boolean(r.description)
    case 'ci': return Boolean(r.ci_id)
    case 'errori': return r.ptr_status === 'errore'
    default: return true
  }
}

/** A DNS TTL in a few characters: «45 s», «15 min», «1 h», «2 h 30 min», «1 g 6 h». */
function durataTtl(secondi: number): string {
  if (secondi < 60) return `${secondi} s`
  if (secondi < 3600) return `${Math.floor(secondi / 60)} min`
  if (secondi < 86400) {
    const h = Math.floor(secondi / 3600)
    const m = Math.floor((secondi % 3600) / 60)
    return m ? `${h} h ${m} min` : `${h} h`
  }
  const g = Math.floor(secondi / 86400)
  const h = Math.floor((secondi % 86400) / 3600)
  return t('{n} g', { n: g }) + (h ? ` ${h} h` : '')
}

/** Where the cached answer comes from and how long it is used: the name's tooltip. */
function dettaglioRisposta(r: AddressRecordInfo): string {
  const parti: string[] = []
  if (r.ptr_source === 'sonda') parti.push(t('fonte: sonda'))
  else if (r.ptr_source === 'control_plane') parti.push(t('fonte: Control Plane'))
  parti.push(r.ptr_ttl ? t('TTL {durata}', { durata: durataTtl(r.ptr_ttl) }) : t('TTL non indicato dal DNS'))
  if (r.ptr_resolved_at) parti.push(t('chiesto il {data}', { data: formatDateTime(r.ptr_resolved_at) }))
  if (r.ptr_valid_until) parti.push(t('valido fino al {data}', { data: formatDateTime(r.ptr_valid_until) }))
  return parti.join(' · ')
}

function NomeDns({ r }: { r: AddressRecordInfo }) {
  if (r.ptr_name) {
    return (
      <>
        <span title={dettaglioRisposta(r)}>{r.ptr_name}</span>
        {/* A failed lookup keeps the last good name: say it was not confirmed. */}
        {r.ptr_error && (
          <>
            {' '}
            <span className="badge badge--warn" title={t('Ultima richiesta non riuscita: {motivo}', { motivo: r.ptr_error })}>
              {t('non aggiornato')}
            </span>
          </>
        )}
      </>
    )
  }
  if (r.ptr_status === 'nessuno') {
    return <span className="text-muted" title={dettaglioRisposta(r)}>{t('nessun record PTR')}</span>
  }
  if (r.ptr_status === 'errore') {
    const titolo = [
      r.ptr_error,
      r.ptr_valid_until ? t('nuovo tentativo dopo il {data}', { data: formatDateTime(r.ptr_valid_until) }) : '',
    ].filter(Boolean).join(' · ')
    return <span className="badge badge--warn" title={titolo || undefined}>{t('non risolto')}</span>
  }
  if (r.scope === 'speciale') {
    return <span className="text-muted" title={t('Multicast, broadcast e indirizzi riservati non hanno un nome DNS.')}>—</span>
  }
  return <span className="text-muted">{t('da risolvere')}</span>
}

function num(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0
}

/** The counts of a reverse-DNS run ({resolved, none, errors}), as a phrase; '' = nothing. */
function descriviPtr(p: Record<string, unknown>): string {
  const parti: string[] = []
  if (num(p.resolved)) parti.push(t('{n} nomi DNS trovati', { n: num(p.resolved) }))
  if (num(p.none)) parti.push(t('{n} senza record PTR', { n: num(p.none) }))
  if (num(p.errors)) parti.push(t('{n} non risolti', { n: num(p.errors) }))
  return parti.join(', ')
}

/** The outcome of «Aggiorna ora» (a full pass: feed, link to CIs, resolve, clean). */
function descriviPassata(r: Record<string, unknown>): Esito {
  const ptr = (r.ptr && typeof r.ptr === 'object' ? r.ptr : {}) as Record<string, unknown>
  const parti: string[] = []
  if (num(r.new)) parti.push(t('{n} indirizzi nuovi', { n: num(r.new) }))
  const nomi = descriviPtr(ptr)
  if (nomi) parti.push(nomi)
  if (num(r.linked)) parti.push(t('{n} ora sono CI', { n: num(r.linked) }))
  if (num(r.removed)) parti.push(t('{n} tolti perché non visti da 90 giorni', { n: num(r.removed) }))
  let testo = parti.length
    ? t('Passata completata: {esito}.', { esito: parti.join(', ') })
    : t('Passata completata: niente di nuovo, i nomi in cache sono ancora validi.')
  const senzaSonda = num(ptr.errors) > 0 && !ptr.probe
  if (senzaSonda) testo += ' ' + t('Nessuna sonda in linea: i nomi interni restano da risolvere.')
  return { tono: senzaSonda ? 'warn' : 'ok', testo }
}

function messaggio(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

export default function AddressInventory() {
  // «Non inventariato» in the traffic views opens the page on that address.
  const [params] = useSearchParams()
  const cercaIniziale = params.get('q') ?? undefined
  const dati = useAsync(() => api.addressInventory(), [])
  // phpIPAM: no connection (or no access to the module) = no «Documenta su IPAM».
  const ipam = useAsync(() => api.ipamServers().catch((): ServerIpam[] => []), [])
  const sonde = useAsync(() => api.probes().catch((): Probe[] => []), [])
  const permessi = usePermessiModuli()
  const scrive = permessi.scrive('addresses')
  const scriveIpam = permessi.scrive('ipam')

  const [vista, setVista] = useState<Vista>('tutti')
  const [inModifica, setInModifica] = useState<string | null>(null)
  const [selezionati, setSelezionati] = useState<Set<string>>(new Set())
  // The running action: 'passata', 'selezionati', 'risolvi:<id>' or 'ipam:<id>'.
  const [lavoro, setLavoro] = useState<string | null>(null)
  const [nota, setNota] = useState<Esito | null>(null)
  const [erroreAzione, setErroreAzione] = useState<unknown>(null)
  // The outcome of a row action, shown in the row (the page banner may be far up).
  const [esiti, setEsiti] = useState<Record<string, Esito>>({})

  const d = dati.data
  const tutte = useMemo(() => d?.addresses ?? [], [d])
  const righe = useMemo(() => tutte.filter((r) => nellaVista(r, vista)), [tutte, vista])

  const serverIpam = ipam.data ?? []
  const ipamConfigurato = serverIpam.length > 0
  const ipamScrivibile = serverIpam.some((s) => s.write_enabled)
  const occupato = lavoro !== null

  const alternaVista = (v: Vista) => setVista((attuale) => (attuale === v ? 'tutti' : v))

  function inizia(chiave: string) {
    setLavoro(chiave)
    setErroreAzione(null)
    setNota(null)
  }

  async function passata() {
    inizia('passata')
    try {
      const r = await api.resolveAddresses([])
      await dati.reload()
      setNota(descriviPassata(r))
    } catch (e) {
      setErroreAzione(e)
    } finally {
      setLavoro(null)
    }
  }

  async function risolviSelezionati() {
    const perId = new Map(tutte.map((r) => [r.id, r.address]))
    const indirizzi = [...selezionati].map((id) => perId.get(id)).filter((a): a is string => Boolean(a)).slice(0, MAX_RISOLVI)
    if (indirizzi.length === 0) return
    inizia('selezionati')
    try {
      const r = await api.resolveAddresses(indirizzi)
      await dati.reload()
      const esito = descriviPtr(r)
      setNota({
        tono: num(r.errors) > 0 ? 'warn' : 'ok',
        testo: esito
          ? t('Nomi DNS chiesti per {n} indirizzi: {esito}.', { n: indirizzi.length, esito })
          : t('Nomi DNS chiesti per {n} indirizzi.', { n: indirizzi.length }),
      })
      setSelezionati(new Set())
    } catch (e) {
      setErroreAzione(e)
    } finally {
      setLavoro(null)
    }
  }

  function esitoRiga(id: string, esito: Esito | null) {
    setEsiti((prima) => {
      const dopo = { ...prima }
      if (esito) dopo[id] = esito
      else delete dopo[id]
      return dopo
    })
  }

  async function risolviUno(r: AddressRecordInfo) {
    inizia(`risolvi:${r.id}`)
    esitoRiga(r.id, null)
    try {
      await api.resolveAddresses([r.address])
      await dati.reload()
    } catch (e) {
      esitoRiga(r.id, { tono: 'error', testo: messaggio(e) })
    } finally {
      setLavoro(null)
    }
  }

  async function documenta(r: AddressRecordInfo) {
    inizia(`ipam:${r.id}`)
    esitoRiga(r.id, null)
    try {
      const esito = await api.ipamDocumentRecord(r.id)
      const fatto = esito.documented[0]
      if (fatto) {
        esitoRiga(r.id, {
          tono: 'ok',
          testo: fatto.subnet
            ? t('Documentato in phpIPAM nella subnet {subnet}.', { subnet: fatto.subnet })
            : t('Documentato in phpIPAM.'),
        })
      } else {
        const motivi = esito.errors.map((e) => e.reason).filter(Boolean).join('; ')
        esitoRiga(r.id, { tono: 'warn', testo: t('Non documentato: {motivo}', { motivo: motivi || t('phpIPAM non ha detto perché') }) })
      }
    } catch (e) {
      esitoRiga(r.id, { tono: 'error', testo: t('Non documentato: {motivo}', { motivo: messaggio(e) }) })
    } finally {
      setLavoro(null)
    }
  }

  const titoloIpam = !ipamScrivibile
    ? t('Accendi “Consenti la scrittura” nella credenziale phpIPAM')
    : !scriveIpam
      ? t('Il tuo ruolo non può scrivere nel modulo IPAM')
      : t('Aggiunge l’indirizzo in phpIPAM, nella subnet che lo contiene, con il nome DNS e la descrizione')

  const colonne = useMemo<Column<AddressRecordInfo>[]>(() => [
    {
      key: 'address',
      header: t('Indirizzo'),
      value: (r) => r.address,
      render: (r) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          <span className="mono">{r.address}</span>{' '}
          <span className={r.scope === 'speciale' ? 'chip chip--muted' : 'chip'}>{etichettaAmbito(r.scope)}</span>
        </span>
      ),
    },
    { key: 'scope', header: t('Ambito'), hidden: true, value: (r) => etichettaAmbito(r.scope) },
    {
      key: 'ptr_name',
      header: t('Nome DNS'),
      value: (r) => etichettaStatoPtr(r),
      // Sort by the name alone: rows without one go last.
      sortValue: (r) => r.ptr_name,
      render: (r) => <NomeDns r={r} />,
    },
    {
      key: 'description',
      header: t('Cos’è'),
      value: (r) => r.description,
      render: (r) => (r.description ? (
        <span
          title={r.description}
          style={{ display: 'inline-block', maxWidth: '36ch', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}
        >
          {r.description}
        </span>
      ) : <span className="text-muted">—</span>),
    },
    {
      key: 'category',
      header: t('Categoria'),
      value: (r) => (r.category ? etichettaCategoria(r.category) : ''),
      render: (r) => (!r.category ? <span className="text-muted">—</span>
        : r.category === 'sospetto' ? <span className="badge badge--warn">{etichettaCategoria(r.category)}</span>
          : etichettaCategoria(r.category)),
    },
    {
      key: 'owner',
      header: t('Responsabile'),
      value: (r) => r.owner,
      render: (r) => r.owner || <span className="text-muted">—</span>,
    },
    {
      key: 'ports',
      header: t('Porte'),
      value: (r) => r.ports.join(' '),
      render: (r) => (r.ports.length === 0 ? <span className="text-muted">—</span> : (
        <span style={{ whiteSpace: 'nowrap' }}>
          <span className="mono">{r.ports.slice(0, PORTE_MOSTRATE).join(', ')}</span>
          {r.ports.length > PORTE_MOSTRATE && (
            <span className="text-muted" title={r.ports.join(', ')}> +{r.ports.length - PORTE_MOSTRATE}</span>
          )}
        </span>
      )),
    },
    {
      key: 'ci',
      header: t('CI'),
      value: (r) => (r.ci_id ? r.ci_name || r.ci_id : t('non inventariato')),
      render: (r) => (r.ci_id
        ? <Link to={`/cis/${r.ci_id}`}>{r.ci_name || r.ci_id.slice(0, 8)}</Link>
        : <span className="text-muted">{t('non inventariato')}</span>),
    },
    { key: 'first_seen', header: t('Prima vista'), type: 'date', hidden: true, value: (r) => r.first_seen, render: (r) => formatDateTime(r.first_seen) },
    { key: 'last_seen', header: t('Ultima vista'), type: 'date', value: (r) => r.last_seen, render: (r) => formatDateTime(r.last_seen) },
    ...(scrive ? [{
      key: 'azioni',
      header: '',
      sort: false,
      searchable: false,
      filter: false,
      value: () => '',
      render: (r: AddressRecordInfo) => {
        // One button in the row (three widened the table past the screen);
        // resolving and documenting are in the panel that opens under it.
        const esito = esiti[r.id]
        const aperto = inModifica === r.id
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
            <button type="button" className="secondary dob-azione" onClick={() => setInModifica(aperto ? null : r.id)}>
              {aperto ? t('Chiudi') : t('Apri')}
            </button>
            {esito && !aperto && (
              <span className={`badge badge--${esito.tono}`} style={{ whiteSpace: 'normal', maxWidth: 220 }}>
                {esito.testo}
              </span>
            )}
          </div>
        )
      },
    }] : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [scrive, esiti, inModifica, lavoro, ipamConfigurato, ipamScrivibile, scriveIpam])

  const s = d?.summary

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Inventario indirizzi')}</h1>
          <p className="page__subtitle">
            {t('Inventario degli indirizzi IP non correlati a un CI censito: sistemi interni non inventariati e servizi esterni raggiunti dalla rete. Per ciascun indirizzo sono disponibili, quando noti, nome DNS, classificazione e informazioni descrittive.')}
          </p>
        </div>
        <div className="page__actions">
          {scrive ? (
            <button
              type="button"
              className="secondary"
              disabled={occupato || dati.loading}
              title={t('Legge subito i flussi, collega gli indirizzi diventati CI e chiede i nomi DNS scaduti, senza aspettare la passata dei 15 minuti')}
              onClick={() => void passata()}
            >
              {lavoro === 'passata' ? t('Aggiorno…') : t('Aggiorna ora')}
            </button>
          ) : (
            <button type="button" className="secondary" disabled={dati.loading} onClick={() => void dati.reload()}>
              {dati.loading ? t('Caricamento…') : t('Aggiorna')}
            </button>
          )}
        </div>
      </header>
      <ErrorBanner error={dati.error} />
      <ErrorBanner error={erroreAzione} />
      {nota && <div className={`banner banner--${nota.tono}`}>{nota.testo}</div>}

      {!d || !s ? (!dati.error && <Spinner />) : (
        <>
          <div className="stats">
            <StatCard label={t('Indirizzi')} value={s.total} onClick={() => setVista('tutti')} />
            <StatCard label={t('Interni')} value={s.internal} onClick={() => alternaVista('interni')} />
            <StatCard label={t('Pubblici')} value={s.public} onClick={() => alternaVista('pubblici')} />
            <StatCard label={t('Con un nome DNS')} value={s.named} onClick={() => alternaVista('con_nome')} />
            <StatCard label={t('Descritti da una persona')} value={s.described} onClick={() => alternaVista('descritti')} />
            <StatCard label={t('Ora sono CI')} value={s.with_ci} onClick={() => alternaVista('ci')} />
            <StatCard
              label={t('Errori di risoluzione')}
              value={s.ptr_errors}
              tone={s.ptr_errors > 0 ? 'warn' : undefined}
              onClick={() => alternaVista('errori')}
            />
          </div>

          <Impostazioni s={d.settings} sonde={sonde.data ?? []} scrive={scrive} onSalvate={() => dati.reload()} />

          <Card>
            {tutte.length === 0 ? (
              <EmptyState
                title={t('Nessun indirizzo ancora')}
                hint={t('Le righe nascono dai flussi (NetFlow, IPFIX, sFlow o la cattura della sonda), ogni 15 minuti: ogni indirizzo delle conversazioni dell’ultimo giorno che non è un CI. Con il filtro del traffico «Entrambi interni» gli indirizzi di internet non vengono tenuti.')}
              />
            ) : (
              <>
                {vista !== 'tutti' && (
                  <div className="banner banner--info" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    {t('Mostro solo: {filtro} ({n}).', { filtro: etichettaVista(vista), n: righe.length })}
                    <button type="button" className="link" onClick={() => setVista('tutti')}>{t('Mostra tutti')}</button>
                  </div>
                )}
                {scrive && selezionati.size > 0 && (
                  <div className="selezione-barra">
                    <strong>{t('{n} indirizzi selezionati', { n: selezionati.size })}</strong>
                    <button type="button" className="secondary" disabled={occupato} onClick={() => void risolviSelezionati()}>
                      {lavoro === 'selezionati' ? t('Risolvo…') : t('Risolvi i selezionati')}
                    </button>
                    {selezionati.size > MAX_RISOLVI && (
                      <span className="text-muted">{t('Si risolvono i primi {max}.', { max: MAX_RISOLVI })}</span>
                    )}
                    <button type="button" className="link" onClick={() => setSelezionati(new Set())}>{t('Annulla la selezione')}</button>
                  </div>
                )}
                <DataTable
                  id="address-inventory"
                  cercaIniziale={cercaIniziale}
                  rows={righe}
                  columns={colonne}
                  rowKey={(r) => r.id}
                  initialSort={{ key: 'last_seen', dir: 'desc' }}
                  conteggio={{ nome: t('indirizzi') }}
                  emptyText={t('Nessun indirizzo per i filtri impostati.')}
                  expand={(r) => (
                    <>
                    <div className="toolbar" style={{ marginBottom: 10, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="secondary"
                        disabled={occupato || r.scope === 'speciale'}
                        title={r.scope === 'speciale'
                          ? t('Multicast, broadcast e indirizzi riservati non hanno un nome DNS.')
                          : t('Chiede di nuovo il nome DNS inverso, senza aspettare la scadenza della cache')}
                        onClick={() => void risolviUno(r)}
                      >
                        {lavoro === `risolvi:${r.id}` ? t('Risolvo…') : t('Risolvi il nome DNS')}
                      </button>
                      {ipamConfigurato && (
                        <button
                          type="button"
                          className="secondary"
                          disabled={occupato || !ipamScrivibile || !scriveIpam}
                          title={titoloIpam}
                          onClick={() => void documenta(r)}
                        >
                          {lavoro === `ipam:${r.id}` ? t('Documento…') : t('Documenta su IPAM')}
                        </button>
                      )}
                      {esiti[r.id] && (
                        <span className={`badge badge--${esiti[r.id].tono}`} style={{ whiteSpace: 'normal' }}>
                          {esiti[r.id].testo}
                        </span>
                      )}
                    </div>
                    <ModificaIndirizzo
                      r={r}
                      onChiudi={() => setInModifica(null)}
                      onSalvato={async () => {
                        await dati.reload()
                        setInModifica(null)
                      }}
                    />
                    </>
                  )}
                  expanded={(r) => r.id === inModifica}
                  selezione={scrive ? { chiavi: selezionati, onCambia: setSelezionati, id: (r) => r.id } : undefined}
                />
              </>
            )}
          </Card>
        </>
      )}
    </>
  )
}

// ------------------------------------------------------------- edit panel

/** What a person writes about an address: what it is, a category, an owner. */
function ModificaIndirizzo({ r, onChiudi, onSalvato }: {
  r: AddressRecordInfo
  onChiudi: () => void
  onSalvato: () => Promise<void>
}) {
  const [descrizione, setDescrizione] = useState(r.description)
  const [categoria, setCategoria] = useState(r.category)
  const [responsabile, setResponsabile] = useState(r.owner)
  const [salvo, setSalvo] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)

  const cambiato = descrizione !== r.description || categoria !== r.category || responsabile !== r.owner

  async function salva() {
    setSalvo(true)
    setErrore(null)
    try {
      await api.updateAddressRecord(r.id, { description: descrizione, category: categoria, owner: responsabile })
      await onSalvato()
    } catch (e) {
      setErrore(e)
      setSalvo(false)
    }
  }

  return (
    <div style={{ maxWidth: 760, padding: '8px 4px' }}>
      <div className="field">
        <label htmlFor={`indirizzo-desc-${r.id}`}>{t('Cos’è {indirizzo}', { indirizzo: r.address })}</label>
        <textarea
          id={`indirizzo-desc-${r.id}`}
          rows={3}
          maxLength={4000}
          autoFocus
          value={descrizione}
          placeholder={t('Cos’è: Office 365, VPN del fornitore X, stampante del reparto…')}
          onChange={(e) => setDescrizione(e.target.value)}
        />
        <div className="field__hint">
          {t('Si vede accanto all’indirizzo nelle viste del traffico: chi lo incontra dopo non deve scoprirlo di nuovo.')}
        </div>
      </div>
      <div className="toolbar">
        <div className="field">
          <label htmlFor={`indirizzo-cat-${r.id}`}>{t('Categoria')}</label>
          <select id={`indirizzo-cat-${r.id}`} value={categoria} onChange={(e) => setCategoria(e.target.value)}>
            <option value="">{t('— nessuna —')}</option>
            {CATEGORIE.map((c) => <option key={c} value={c}>{etichettaCategoria(c)}</option>)}
          </select>
        </div>
        <div className="field" style={{ flex: 1, minWidth: 220 }}>
          <label htmlFor={`indirizzo-resp-${r.id}`}>{t('Responsabile')}</label>
          <input
            id={`indirizzo-resp-${r.id}`}
            type="text"
            maxLength={200}
            value={responsabile}
            placeholder={t('chi lo conosce: una persona, un reparto, un fornitore')}
            onChange={(e) => setResponsabile(e.target.value)}
          />
        </div>
      </div>
      <ErrorBanner error={errore} />
      <div className="toolbar" style={{ alignItems: 'center', marginBottom: 0 }}>
        <button type="button" onClick={() => void salva()} disabled={!cambiato || salvo}>
          {salvo ? t('Salvataggio…') : t('Salva')}
        </button>
        <button type="button" className="secondary" onClick={onChiudi} disabled={salvo}>{t('Annulla')}</button>
        <span className="text-muted" style={{ fontSize: 13, marginLeft: 'auto' }}>
          {/* Who only: updated_at moves at every pass (last seen, PTR), so it is not when a person wrote. */}
          {r.updated_by
            ? r.described_at
              ? t('Ultima modifica di {chi} il {quando}.', { chi: r.updated_by, quando: formatDateTime(r.described_at) })
              : t('Ultima modifica di {chi}.', { chi: r.updated_by })
            : t('Nessuno l’ha ancora descritto.')}
        </span>
      </div>
    </div>
  )
}

// --------------------------------------------------------------- settings

/** How the names are resolved: on/off, public addresses too, which probe, which DNS server. */
function Impostazioni({ s, sonde, scrive, onSalvate }: {
  s: AddressInventorySettings
  sonde: Probe[]
  scrive: boolean
  onSalvate: () => Promise<void>
}) {
  const [aperto, setAperto] = useState(false)
  const [risolvi, setRisolvi] = useState(s.resolve)
  const [pubblici, setPubblici] = useState(s.resolve_public)
  const [sonda, setSonda] = useState(s.probe_id)
  const [server, setServer] = useState(s.dns_server)
  const [salvo, setSalvo] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const [fatto, setFatto] = useState(false)

  useEffect(() => {
    setRisolvi(s.resolve)
    setPubblici(s.resolve_public)
    setSonda(s.probe_id)
    setServer(s.dns_server)
  }, [s.resolve, s.resolve_public, s.probe_id, s.dns_server])

  const cambiato = risolvi !== s.resolve || pubblici !== s.resolve_public || sonda !== s.probe_id
    || server.trim() !== s.dns_server
  const bloccato = !scrive || salvo
  const sondaScelta = sonde.find((p) => p.id === s.probe_id)

  async function salva() {
    setSalvo(true)
    setErrore(null)
    setFatto(false)
    try {
      await api.saveAddressInventorySettings({
        resolve: risolvi, resolve_public: pubblici, probe_id: sonda, dns_server: server.trim(),
      })
      setFatto(true)
      await onSalvate()
    } catch (e) {
      setErrore(e)
    } finally {
      setSalvo(false)
    }
  }

  const riassunto = !s.resolve
    ? t('Risoluzione spenta: i nomi DNS non vengono chiesti.')
    : [
        s.resolve_public ? t('Risoluzione accesa, anche per gli indirizzi pubblici') : t('Risoluzione accesa, solo per gli indirizzi interni'),
        t('sonda: {nome}', {
          nome: !s.probe_id ? t('la più recente in linea') : sondaScelta?.name ?? t('sonda non più presente'),
        }),
        t('server DNS: {server}', { server: s.dns_server || t('il resolver della sonda') }),
      ].join(' · ')

  return (
    <Card
      title={t('Risoluzione dei nomi DNS')}
      actions={(
        <button type="button" className="link" aria-expanded={aperto} onClick={() => setAperto((v) => !v)}>
          {aperto ? t('Nascondi') : t('Mostra')}
        </button>
      )}
    >
      {!aperto ? (
        <p className="text-muted" style={{ margin: 0 }}>{riassunto}</p>
      ) : (
        <>
          <p className="field__hint" style={{ marginTop: 0, marginBottom: 16 }}>
            {t('I nomi restano in cache per il TTL della risposta DNS (fra 15 minuti e 7 giorni). I nomi interni li chiede una sonda, perché solo lei raggiunge il DNS della rete; per gli indirizzi pubblici, se nessuna sonda risponde, li chiede il Control Plane. Cache e impostazioni sono di questo tenant.')}
          </p>
          <div className="checkbox">
            <input id="indirizzi-risolvi" type="checkbox" checked={risolvi} disabled={bloccato}
              onChange={(e) => { setRisolvi(e.target.checked); setFatto(false) }} />
            <label htmlFor="indirizzi-risolvi">
              {t('Risolvi i nomi DNS inversi')}
              <small>
                {t('A ogni passata (ogni 15 minuti) si chiede il record PTR degli indirizzi con la risposta scaduta, al più 500, i visti più di recente per primi.')}
              </small>
            </label>
          </div>
          <div className="checkbox">
            <input id="indirizzi-pubblici" type="checkbox" checked={pubblici} disabled={bloccato || !risolvi}
              onChange={(e) => { setPubblici(e.target.checked); setFatto(false) }} />
            <label htmlFor="indirizzi-pubblici">
              {t('anche per gli indirizzi pubblici')}
              <small>{t('I servizi internet con cui parla la rete. Spenta, si chiedono solo i nomi degli indirizzi interni.')}</small>
            </label>
          </div>
          <div className="toolbar">
            <div className="field" style={{ minWidth: 240 }}>
              <label htmlFor="indirizzi-sonda">{t('Sonda che chiede i nomi')}</label>
              <select id="indirizzi-sonda" value={sonda} disabled={bloccato || !risolvi}
                onChange={(e) => { setSonda(e.target.value); setFatto(false) }}>
                <option value="">{t('la più recente in linea')}</option>
                {sonde.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}{p.status === 'online' ? '' : ` — ${t('non in linea')}`}
                  </option>
                ))}
                {s.probe_id && !sondaScelta && <option value={s.probe_id}>{t('sonda non più presente')}</option>}
              </select>
            </div>
            <div className="field" style={{ minWidth: 240 }}>
              <label htmlFor="indirizzi-dns">{t('Server DNS')}</label>
              <input id="indirizzi-dns" type="text" className="mono" maxLength={60} value={server}
                disabled={bloccato || !risolvi} placeholder={t('vuoto: il resolver della sonda')}
                onChange={(e) => { setServer(e.target.value); setFatto(false) }} />
              <div className="field__hint">{t('Indirizzo IP, anche con la porta (10.0.0.53:5353).')}</div>
            </div>
          </div>
          <ErrorBanner error={errore} />
          <div className="toolbar" style={{ alignItems: 'center', marginBottom: 0 }}>
            {scrive ? (
              <button type="button" onClick={() => void salva()} disabled={!cambiato || salvo}>
                {salvo ? t('Salvataggio…') : t('Salva')}
              </button>
            ) : (
              <span className="text-muted">{t('Hai la sola lettura dell’inventario indirizzi: le impostazioni non si possono cambiare.')}</span>
            )}
            {fatto && <span className="badge badge--ok">{t('Salvato')}</span>}
          </div>
        </>
      )}
    </Card>
  )
}
